import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BookOpen,
  GitBranch,
  Images,
  LockKeyhole,
  ArrowUpRight,
  Mail,
  Leaf,
  Users,
  Search,
  Check,
} from "lucide-react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import { auth, friendlyError } from "./api";
import { useAuth } from "./state";
import { useI18n, PRODUCT_NAME } from "./i18n";
import { Arrow, Brand, Button, ErrorBox, Field } from "./ui";
export function LanguageButton() {
  const { language, setLanguage, t } = useI18n();
  return (
    <button
      className="language-button"
      onClick={() => setLanguage(language === "en" ? "he" : "en")}
      aria-label={t("Switch to Hebrew", "מעבר לאנגלית")}
    >
      {language === "en" ? "עברית" : "English"}
    </button>
  );
}
export function PublicHeader() {
  const { language, t } = useI18n();
  const prefix = language === "he" ? "/he" : "";
  return (
    <header className="public-header">
      <Link to={prefix || "/"} aria-label={PRODUCT_NAME}>
        <Brand />
      </Link>
      <nav aria-label={t("Main navigation", "ניווט ראשי")}>
        <Link to={`${prefix}/features`}>
          {t("The possibilities", "האפשרויות")}
        </Link>
        <LanguageButton />
        <Link className="public-login" to={`${prefix}/login`}>
          {t("Log in", "כניסה")}
        </Link>
        <Link className="button small" to={`${prefix}/signup`}>
          {t("Begin your family archive", "יצירת ארכיון משפחתי")}
          <Arrow />
        </Link>
      </nav>
    </header>
  );
}
export function PublicPage({ features = false }: { features?: boolean }) {
  const { t, language } = useI18n();
  const prefix = language === "he" ? "/he" : "";
  useEffect(() => {
    document.title = `${features ? t("Features", "אפשרויות") : t("A home for your family story", "בית לסיפור המשפחתי שלכם")} · ${PRODUCT_NAME}`;
  }, [language, features]);
  const cards = [
    {
      Icon: Images,
      n: "01",
      title: t("Every photograph, a connection.", "בכל תצלום, קשר."),
      text: t(
        "Bring your photographs together. Add names, places, dates, and albums so the details travel with the memory.",
        "רכזו את התצלומים שלכם. הוסיפו שמות, מקומות, תאריכים ואלבומים, כדי שהפרטים יישארו עם הזיכרון.",
      ),
    },
    {
      Icon: GitBranch,
      n: "02",
      title: t("See where your stories meet.", "גלו היכן הסיפורים נפגשים."),
      text: t(
        "Build a connected family tree with real people and relationships. Organize branches without separating the family.",
        "בנו עץ משפחה מחובר עם אנשים וקשרים אמיתיים. ארגנו ענפים בלי להפריד את המשפחה.",
      ),
    },
    {
      Icon: BookOpen,
      n: "03",
      title: t("Keep the words, too.", "שמרו גם את המילים."),
      text: t(
        "A favourite recipe. A journey remembered. A life in someone’s own words. Give every story room to unfold.",
        "מתכון אהוב. מסע שנזכרים בו. חיים במילותיו של אדם קרוב. תנו לכל סיפור מקום להתפתח.",
      ),
    },
  ];
  return (
    <div className="public-site">
      <PublicHeader />
      <main id="main-content">
        <section className="public-hero">
          <div className="hero-copy">
            <p className="eyebrow">
              <span className="small-rule" />
              {t("FOR THE GENERATIONS TO COME", "למען הדורות הבאים")}
            </p>
            <h1>
              {features
                ? t(
                    "More than an archive. A living connection.",
                    "יותר מארכיון. קשר חי.",
                  )
                : t("Your family story,", "הסיפור המשפחתי שלכם,")}{" "}
              {!features && <em>{t("beautifully kept.", "נשמר באהבה.")}</em>}
            </h1>
            <p className="hero-description">
              {t(
                "A private, thoughtful home for the photographs, people, and stories that make your family yours.",
                "בית פרטי ומוקפד לתצלומים, לאנשים ולסיפורים שהופכים את המשפחה שלכם למי שהיא.",
              )}
            </p>
            <div className="hero-actions">
              <Link className="button" to={`${prefix}/signup`}>
                {t("Create your family workspace", "יצירת המרחב המשפחתי")}
                <Arrow />
              </Link>
              <Link
                className="text-link"
                to={features ? `${prefix}/login` : `${prefix}/features`}
              >
                {features
                  ? t("Already have a workspace?", "כבר יש לכם מרחב?")
                  : t("Take a closer look", "גלו עוד")}
                <ArrowUpRight size={17} />
              </Link>
            </div>
            <div className="hero-assurance">
              <LockKeyhole size={15} />
              {t(
                "Private by invitation. Shared across generations.",
                "פרטי ובהזמנה בלבד. משותף לדורות.",
              )}
            </div>
          </div>
          <div className="heritage-illustration" aria-hidden="true">
            <div className="illustration-frame">
              <div className="arch">
                <Leaf size={112} strokeWidth={0.6} />
                <div className="arch-rule" />
                <span>
                  {t("THE STORIES", "הסיפורים")}
                  <br />
                  <i>{t("we carry", "שאנו נושאים")}</i>
                </span>
                <div className="arch-rule" />
                <small>
                  {t("PAST · PRESENT · TOGETHER", "עבר · הווה · יחד")}
                </small>
              </div>
            </div>
            <span className="illustration-note">
              {t("A place for what matters.", "מקום למה שחשוב.")}
            </span>
          </div>
        </section>
        <section className="public-intro">
          <span className="eyebrow">
            {t(
              "ONE FAMILY. SO MANY STORIES.",
              "משפחה אחת. כל כך הרבה סיפורים.",
            )}
          </span>
          <h2>
            {t("The little details become", "הפרטים הקטנים הופכים")}
            <em>{t(" the things we treasure.", " לאוצרות שלנו.")}</em>
          </h2>
        </section>
        <section className="feature-grid">
          {cards.map(({ Icon, n, title, text }) => (
            <article key={n}>
              <div className="feature-top">
                <Icon size={30} strokeWidth={1} />
                <span>{n}</span>
              </div>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </section>
        {features && (
          <section className="feature-detail">
            <h2>
              {t("Made for keeping, and for sharing.", "נוצר לשימור ולשיתוף.")}
            </h2>
            <div>
              {[
                {
                  Icon: LockKeyhole,
                  title: t("A truly private workspace", "מרחב פרטי באמת"),
                  text: t(
                    "Only invited members can access your archive. Your family’s identity and content never appear on our public pages.",
                    "רק חברים שהוזמנו יכולים לגשת לארכיון. זהות המשפחה והתוכן שלה אינם מופיעים בעמודים הציבוריים.",
                  ),
                },
                {
                  Icon: Users,
                  title: t("Contribute together", "תורמים יחד"),
                  text: t(
                    "Every member can add, edit, and restore memories. Owners manage invitations, members, and appearance.",
                    "כל חבר יכול להוסיף, לערוך ולשחזר זיכרונות. מנהלי המרחב שולטים בהזמנות, בחברות ובמראה.",
                  ),
                },
                {
                  Icon: Search,
                  title: t(
                    "Find the details you remember",
                    "מצאו את הפרטים שזכרתם",
                  ),
                  text: t(
                    "Filter your archive by people, albums, names, dates, places, and tags. Preserve original English and Hebrew text.",
                    "סננו את הארכיון לפי אנשים, אלבומים, שמות, תאריכים, מקומות ותגיות. שמרו טקסט מקורי באנגלית ובעברית.",
                  ),
                },
                {
                  Icon: Check,
                  title: t("A history you can recover", "היסטוריה שאפשר לשחזר"),
                  text: t(
                    "Attributable changes and recoverable versions help your family keep its history with confidence.",
                    "שינויים מתועדים וגרסאות שניתנות לשחזור עוזרים למשפחה לשמור על ההיסטוריה בביטחון.",
                  ),
                },
              ].map(({ Icon, title, text }) => (
                <article key={title}>
                  <Icon />
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </section>
        )}
        <section className="public-bottom">
          <Leaf size={35} strokeWidth={1} />
          <h2>
            {t("Start with a name.", "מתחילים בשם.")}
            <br />
            <em>{t("Build something lasting.", "בונים משהו שנשאר.")}</em>
          </h2>
          <p>
            {t(
              "Name your workspace, make it your own, and add the first people to your family tree.",
              "תנו שם למרחב, התאימו אותו למשפחה, והוסיפו את האנשים הראשונים לעץ המשפחה.",
            )}
          </p>
          <Link className="button" to={`${prefix}/signup`}>
            {t("Begin your family archive", "יצירת ארכיון משפחתי")}
            <Arrow />
          </Link>
        </section>
      </main>
      <footer className="public-footer">
        <Brand compact />
        <span>{PRODUCT_NAME}</span>
        <span>
          {t("Made for the memories that matter.", "לזיכרונות שחשובים באמת.")}
        </span>
      </footer>
    </div>
  );
}
export function AuthPage({ signup = false }: { signup?: boolean }) {
  const { t, language } = useI18n();
  const prefix = language === "he" ? "/he" : "";
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const token = new URLSearchParams(location.search).get("invite");
    if (token) sessionStorage.setItem("heritage-invitation", token);
  }, [location.search]);
  useEffect(() => {
    document.title = `${signup ? t("Create an account", "יצירת חשבון") : t("Welcome back", "ברוכים השבים")} · ${PRODUCT_NAME}`;
  }, [language, signup]);
  async function proceed() {
    await refresh();
    const invite = sessionStorage.getItem("heritage-invitation");
    navigate(invite ? `/invite?token=${encodeURIComponent(invite)}` : "/app");
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (signup) {
        const result = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );
        if (name) await updateProfile(result.user, { displayName: name });
        await sendEmailVerification(result.user);
      } else await signInWithEmailAndPassword(auth, email, password);
      await proceed();
    } catch (e) {
      setError(friendlyError(e, language === "he"));
    } finally {
      setBusy(false);
    }
  }
  async function google() {
    setBusy(true);
    setError("");
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      await proceed();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="public-site">
      <PublicHeader />
      <main id="main-content" className="auth-layout">
        <div className="auth-story">
          <p className="eyebrow">
            {t("A PLACE TO BELONG", "מקום להשתייך אליו")}
          </p>
          <h1>
            {t("Every family has", "לכל משפחה יש")}
            <em>{t(" a story worth keeping.", " סיפור שכדאי לשמור.")}</em>
          </h1>
          <div className="line-leaf">
            <Leaf size={72} strokeWidth={0.7} />
          </div>
          <p>
            {t(
              "Photographs find their names. Stories find their listeners. Generations find each other.",
              "התצלומים מוצאים את שמותיהם. הסיפורים מוצאים אוזן קשבת. הדורות מוצאים זה את זה.",
            )}
          </p>
        </div>
        <section className="auth-card">
          <h2>
            {signup
              ? t("Begin your story", "התחילו את הסיפור שלכם")
              : t("Welcome back", "ברוכים השבים")}
          </h2>
          <p>
            {signup
              ? t(
                  "Create your account, then your family’s own private space.",
                  "צרו חשבון, ואחריו את המרחב הפרטי של המשפחה.",
                )
              : t(
                  "Your family’s memories are waiting for you.",
                  "הזיכרונות המשפחתיים מחכים לכם.",
                )}
          </p>
          <ErrorBox error={error} />
          {notice && (
            <p className="notice" role="status">
              {notice}
            </p>
          )}
          {user ? (
            <Button onClick={() => void proceed()}>
              {t("Continue to your workspace", "המשך למרחב המשפחתי")}
              <Arrow />
            </Button>
          ) : (
            <>
              <form onSubmit={submit}>
                {signup && (
                  <Field label={t("Your name", "השם שלכם")}>
                    <input
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </Field>
                )}
                <Field label={t("Email address", "כתובת דוא״ל")}>
                  <input
                    type="email"
                    autoComplete="email"
                    dir="ltr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </Field>
                <Field
                  label={t("Password", "סיסמה")}
                  hint={
                    signup
                      ? t("At least 8 characters.", "לפחות 8 תווים.")
                      : undefined
                  }
                >
                  <input
                    type="password"
                    autoComplete={signup ? "new-password" : "current-password"}
                    minLength={signup ? 8 : undefined}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </Field>
                <Button type="submit" disabled={busy}>
                  {busy
                    ? t("Please wait…", "אנא המתינו…")
                    : signup
                      ? t("Create account", "יצירת חשבון")
                      : t("Log in", "כניסה")}
                  <Arrow />
                </Button>
              </form>
              {!signup && (
                <button
                  className="text-link forgot"
                  disabled={busy}
                  onClick={async () => {
                    if (!email) {
                      setError(
                        t(
                          "Enter your email address first.",
                          "הזינו תחילה כתובת דוא״ל.",
                        ),
                      );
                      return;
                    }
                    try {
                      await sendPasswordResetEmail(auth, email);
                      setNotice(
                        t(
                          "If this account exists, a reset email is on its way.",
                          "אם החשבון קיים, הודעת איפוס תישלח בדוא״ל.",
                        ),
                      );
                    } catch (e) {
                      setError(friendlyError(e));
                    }
                  }}
                >
                  {t("Forgot your password?", "שכחתם את הסיסמה?")}
                </button>
              )}
              <div className="form-divider">
                <span>{t("or", "או")}</span>
              </div>
              <Button
                kind="secondary"
                disabled={busy}
                onClick={() => void google()}
              >
                <Mail size={17} />
                {t("Continue with Google", "המשך עם Google")}
              </Button>
            </>
          )}
          <p className="auth-switch">
            {signup
              ? t("Already part of the family?", "כבר יש לכם חשבון?")
              : t("New here?", "חדשים כאן?")}{" "}
            <Link to={`${prefix}/${signup ? "login" : "signup"}`}>
              {signup
                ? t("Log in", "כניסה")
                : t("Create an account", "יצירת חשבון")}
            </Link>
          </p>
          <div className="privacy-note">
            <LockKeyhole size={15} />
            {t(
              "Your archive stays private. Membership is always by invitation.",
              "הארכיון שלכם נשאר פרטי. הצטרפות תמיד בהזמנה.",
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
